import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'recompose';
import { Container, Dropdown, Icon, Menu } from 'semantic-ui-react';

import hasJSONAPIClient from '../../hocs/has-jsonapi-client';
import hasRESTClient from '../../hocs/has-rest-client';
import hasOrgansation from '../../hocs/has-organisation';
import withTrackedAssetsData from '../../hocs/data/with-tracked-assets-data';

import CampaignHeader from './campaign-header';
import InfluencersTab from './influencers-tab';
import SettingsTab from './settings-tab';
import ReportsDropdown from './reports-dropdown';
import AddJumboContentModal, { handleField } from './add-jumbo-content-modal';
import { MIN_SEARCH_LENGTH, PAGE_SIZE } from './constants';

const JUMBO_TYPE = 'JUMBO';
const SEARCH_DEBOUNCE_MS = 350;
const SETTINGS_TAB = 'settings';
const INSTAGRAM_PLATFORM = 'INSTAGRAM';

const addContentTrigger = <span><Icon name="plus" />Add content</span>;

// Page and search live in the URL (page is 1-based) so a view can be shared.
const pageFromProps = props => {
  const raw = parseInt(_.get(props, 'location.query.page', '1'), 10);
  return Number.isNaN(raw) || raw < 1 ? 0 : raw - 1;
};

const searchFromProps = props => _.get(props, 'location.query.q', '');

// '' = all, 'true' = posted, 'false' = not posted
const postedFromProps = props => _.get(props, 'location.query.posted', '');

const tabFromProps = props => _.get(props, 'location.query.tab', '');

class LargeCampaign extends Component {
  constructor(props) {
    super(props);
    this.state = {
      campaign: {},
      creators: [],
      total: null,
      isLoading: true,
      hasFailed: false,
      sort: 'desc',
      searchInput: searchFromProps(props),
      addContentPlatform: null,
    };
  }

  componentDidMount() {
    this.loadCampaign();
    if (tabFromProps(this.props) !== SETTINGS_TAB) this.loadCreators();
  }

  componentWillReceiveProps(nextProps) {
    const pageChanged = pageFromProps(nextProps) !== pageFromProps(this.props);
    const searchChanged = searchFromProps(nextProps) !== searchFromProps(this.props);
    const postedChanged = postedFromProps(nextProps) !== postedFromProps(this.props);
    const leftSettings = tabFromProps(this.props) === SETTINGS_TAB
      && tabFromProps(nextProps) !== SETTINGS_TAB;

    if (searchChanged) this.setState({ searchInput: searchFromProps(nextProps) });
    if (pageChanged || searchChanged || postedChanged || leftSettings) this.loadCreators(nextProps);
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.pushSearch.cancel();
  }

  onPageChange = page => this.updateQuery({ page: page + 1 });

  onTabChange = tab => this.updateQuery({ tab: tab || undefined });

  onPostedChange = (e, { value }) => this.updateQuery({
    posted: value === 'all' ? undefined : value,
    page: 1,
  });

  onAddContent = (e, { value }) => this.setState({ addContentPlatform: value });

  onCloseAddContent = () => this.setState({ addContentPlatform: null });

  // Save the handle onto the creator when it is new, then attach the post to their collaboration.
  onAttachContent = async ({ creator, details }) => {
    const { jsonAPIClient, params: { id: campaignId } } = this.props;
    const field = handleField(details.platform);
    const influencerId = _.get(creator, 'influencer.id');
    const current = _.get(creator, `influencer.${field}`);
    const needsHandle = Boolean(details.handle) && _.toLower(current || '') !== _.toLower(details.handle);
    const isInstagram = details.platform === INSTAGRAM_PLATFORM;

    // instagramAccountId is required on Instagram assets but the creators endpoint does not
    // return instagramId, so read it off the influencer resource.
    let instagramAccountId = _.get(creator, 'influencer.instagramId');
    if (influencerId && (needsHandle || (isInstagram && !instagramAccountId))) {
      const influencer = await jsonAPIClient.get('influencer', influencerId);
      instagramAccountId = influencer.toJSON().instagramId;
      if (needsHandle) {
        influencer.set(field, details.handle);
        await influencer.sync();
      }
    }

    const trackedAsset = await this.props.createTrackedAsset({
      assetId: details.assetId,
      assetURL: details.assetURL,
      discussionId: creator.id,
      campaignId,
      discussionName: creator.name,
      instagramAccountId,
      platform: details.platform,
      assetType: details.assetType,
    });

    await this.props.syncTrackedAssetAndDiscussion({
      discussionId: creator.id,
      trackedAssetId: trackedAsset.id,
    });
  };

  onContentAttached = () => {
    this.setState({ addContentPlatform: null }, () => this.loadCreators());
  };

  // Same target as the collaboration cards elsewhere; :collabId is the discussion id.
  onCreatorSelect = discussionId => {
    const { params: { id: campaignId } } = this.props;
    this.context.router.push({
      pathname: `/campaign/${campaignId}/collaboration/${discussionId}`,
    });
  };

  // PATCH only the edited fields. The JSON:API client's sync() echoes the whole resource
  // back, relationships included, and campaigns carrying stale discussion links fail on write.
  onSettingsSave = attributes => {
    const { restClient, params: { id: campaignId } } = this.props;
    const edited = _.omitBy(attributes, _.isNull);
    return restClient.patch(`campaigns/${campaignId}`, { data: { attributes: edited } })
      .then(() => this.loadCampaign());
  };

  // Typing updates the field immediately; the URL (and the fetch) follow on a debounce.
  onSearchChange = (e, { value }) => {
    this.setState({ searchInput: value });
    this.pushSearch(value);
  };

  onSearchClear = () => {
    this.setState({ searchInput: '' });
    this.pushSearch.cancel();
    this.updateQuery({ q: undefined, page: 1 });
  };

  // Toggle last-scanned direction and reload from the first page. Already on page 1 means the
  // URL would not change, so refetch directly rather than waiting on componentWillReceiveProps.
  onSortChange = () => {
    const sort = this.state.sort === 'desc' ? 'asc' : 'desc';
    this.setState({ sort }, () => {
      if (pageFromProps(this.props) === 0) this.loadCreators();
      else this.updateQuery({ page: 1 });
    });
  };

  // A search below the minimum is sent as empty so the API browses rather than filters.
  pushSearch = _.debounce(value => {
    const q = _.size(_.trim(value)) >= MIN_SEARCH_LENGTH ? _.trim(value) : undefined;
    if (q === (searchFromProps(this.props) || undefined)) return;
    this.updateQuery({ q, page: 1 });
  }, SEARCH_DEBOUNCE_MS);

  updateQuery = changes => {
    const { location } = this.props;
    const query = _.omitBy({ ...location.query, ...changes }, _.isUndefined);
    this.context.router.push({ pathname: location.pathname, query });
  };

  setSafely = state => {
    if (!this.isUnmounted) this.setState(state);
  };

  // Gate on the campaign type: only JUMBO campaigns belong on this page.
  loadCampaign = () => {
    const { jsonAPIClient, params: { id: campaignId } } = this.props;
    jsonAPIClient.get('campaigns', campaignId)
      .then(resource => {
        const campaign = resource.toJSONTree();
        if (campaign.campaignType !== JUMBO_TYPE) {
          this.context.router.replace('/');
          return;
        }
        this.setSafely({ campaign });
      })
      .catch(() => this.context.router.replace('/'));
  };

  // One call covers the page, the total and the handle search.
  loadCreators = (props = this.props) => {
    const { campaignAPIClient, params: { id: campaignId } } = props;
    const page = pageFromProps(props) + 1;
    const search = searchFromProps(props);
    const requestId = _.uniqueId();

    this.latestRequestId = requestId;
    this.setState({ isLoading: true, hasFailed: false });

    campaignAPIClient.get(`/jumbo/campaigns/${campaignId}/creators`, {
      params: {
        page,
        perPage: PAGE_SIZE,
        order: this.state.sort,
        search: search || undefined,
        hasPosted: postedFromProps(props) || undefined,
      },
    })
      .then(response => {
        if (this.latestRequestId !== requestId) return; // a newer request won
        const payload = _.get(response, 'data.data', {});
        this.setSafely({
          creators: _.get(payload, 'data', []),
          total: _.get(payload, 'metadata.total', 0),
          isLoading: false,
        });
      })
      .catch(() => {
        if (this.latestRequestId !== requestId) return;
        this.setSafely({ creators: [], total: null, isLoading: false, hasFailed: true });
      });
  };

  render() {
    const {
      campaign, creators, total, isLoading, hasFailed, sort, searchInput, addContentPlatform,
    } = this.state;
    const { organisation } = this.props;
    const isSettings = tabFromProps(this.props) === SETTINGS_TAB;

    return (
      <Container style={{ marginTop: '2rem' }}>
        <CampaignHeader campaign={campaign} />

        <Menu pointing secondary>
          <Menu.Item active={!isSettings} onClick={() => this.onTabChange('')}>Influencers</Menu.Item>
          <Menu.Item active={isSettings} onClick={() => this.onTabChange(SETTINGS_TAB)}>Settings</Menu.Item>
          <Menu.Menu position="right">
            <Dropdown item trigger={addContentTrigger} disabled={!campaign.id}>
              <Dropdown.Menu>
                <Dropdown.Item value="tiktok" onClick={this.onAddContent} icon="video" text="Add TikTok video" />
                <Dropdown.Item
                  value="instagram"
                  onClick={this.onAddContent}
                  icon="instagram"
                  text="Add Instagram post"
                />
              </Dropdown.Menu>
            </Dropdown>
            <ReportsDropdown campaign={campaign} organisation={organisation} />
          </Menu.Menu>
        </Menu>

        {isSettings
          ? <SettingsTab key={campaign.id || 'loading'} campaign={campaign} onSave={this.onSettingsSave} />
          : <InfluencersTab
            creators={creators}
            page={pageFromProps(this.props)}
            pageSize={PAGE_SIZE}
            total={total}
            isLoading={isLoading}
            hasFailed={hasFailed}
            onPageChange={this.onPageChange}
            sort={sort}
            onSortChange={this.onSortChange}
            searchInput={searchInput}
            searchTerm={searchFromProps(this.props)}
            posted={postedFromProps(this.props)}
            onPostedChange={this.onPostedChange}
            onSearchChange={this.onSearchChange}
            onSearchClear={this.onSearchClear}
            onCreatorSelect={this.onCreatorSelect}
          />}

        {addContentPlatform &&
          <AddJumboContentModal
            campaignId={this.props.params.id}
            platform={addContentPlatform}
            campaignAPIClient={this.props.campaignAPIClient}
            onAttach={this.onAttachContent}
            onAttached={this.onContentAttached}
            onClose={this.onCloseAddContent}
          />}
      </Container>
    );
  }
}

LargeCampaign.contextTypes = {
  router: PropTypes.object.isRequired,
};

LargeCampaign.propTypes = {
  params: PropTypes.shape({ id: PropTypes.string }).isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string, query: PropTypes.object }).isRequired,
  jsonAPIClient: PropTypes.shape({ get: PropTypes.func }).isRequired,
  restClient: PropTypes.shape({ patch: PropTypes.func }).isRequired,
  campaignAPIClient: PropTypes.shape({ get: PropTypes.func }).isRequired,
  organisation: PropTypes.object,
  createTrackedAsset: PropTypes.func.isRequired,
  syncTrackedAssetAndDiscussion: PropTypes.func.isRequired,
};

LargeCampaign.defaultProps = {
  organisation: {},
};

export default compose(
  hasJSONAPIClient,
  hasRESTClient,
  hasOrgansation,
  withTrackedAssetsData,
)(LargeCampaign);



/** WEBPACK FOOTER **
 ** ./src/containers/large-campaign/index.js
 **/